// The right panel's width on a project page (PBJ and Penyedia), from 1024px
// with the panel beside the page (project.css shows the handle only there):
// drag the panel's left edge, or focus the separator and use the arrow keys
// (Shift for bigger steps, Home and End for the limits); a double click goes
// back to the default. The width is a CSS variable on .pk-layout (--pj-aside-w), kept
// per browser in localStorage. Pointer moves are divided by the page's
// scale (rendered over CSS width), so a root zoom, should one come back,
// still moves the edge with the pointer.
import { useCallback, useEffect, useRef, useState } from "react";
import { PANEL_ID } from "./SidePanel";

export const PANEL_WIDTH_KEY = "procureai.project.panel-width";
/** 320 and 288: the old 360 and 320 as the 90% zoom rendered them (Fase 5 removed the zoom). */
export const PANEL_DEFAULT = 320;
export const PANEL_MIN = 288;
export const PANEL_MAX = 720;
/** The page column keeps at least this much beside the panel. */
const MAIN_MIN = 560;
const STEP = 16;
const BIG_STEP = 64;

function readWidth(): number | null {
  try {
    const raw = localStorage.getItem(PANEL_WIDTH_KEY);
    const n = raw === null ? NaN : Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function writeWidth(w: number | null): void {
  try {
    if (w === null) localStorage.removeItem(PANEL_WIDTH_KEY);
    else localStorage.setItem(PANEL_WIDTH_KEY, String(Math.round(w)));
  } catch {
    // The width just is not remembered.
  }
}

/** The largest width the layout allows: PANEL_MAX, less when the page column would drop under MAIN_MIN. */
export function panelMax(layoutWidth: number): number {
  if (!(layoutWidth > 0)) return PANEL_MAX;
  return Math.max(PANEL_MIN, Math.min(PANEL_MAX, layoutWidth - MAIN_MIN));
}

export function clampWidth(w: number, max: number): number {
  return Math.round(Math.min(max, Math.max(PANEL_MIN, w)));
}

export function PanelResizer() {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState<number>(() => readWidth() ?? PANEL_DEFAULT);
  const [max, setMax] = useState(PANEL_MAX);
  const drag = useRef<{ x: number; start: number; scale: number } | null>(null);

  const layout = () => ref.current?.parentElement ?? null;

  // The layout's width sets the limit; it follows the window.
  useEffect(() => {
    const el = layout();
    if (!el) return;
    const measure = () => setMax(panelMax(el.offsetWidth));
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const shown = clampWidth(width, max);
  useEffect(() => {
    const el = layout();
    if (!el) return;
    el.style.setProperty("--pj-aside-w", `${shown}px`);
    return () => {
      el.style.removeProperty("--pj-aside-w");
    };
  }, [shown]);

  const set = useCallback(
    (w: number, persist: boolean) => {
      const next = clampWidth(w, max);
      setWidth(next);
      if (persist) writeWidth(next);
    },
    [max],
  );

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();
    const aside = document.getElementById(PANEL_ID);
    const css = aside?.offsetWidth || shown;
    const drawn = aside?.getBoundingClientRect().width || css;
    drag.current = { x: e.clientX, start: css, scale: drawn / css || 1 };
    e.currentTarget.setPointerCapture?.(e.pointerId);
    e.currentTarget.dataset.dragging = "";
  };
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d) return;
    // The panel is on the right: moving left widens it.
    set(d.start - (e.clientX - d.x) / d.scale, false);
  };
  const end = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!drag.current) return;
    drag.current = null;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    delete e.currentTarget.dataset.dragging;
    writeWidth(shown);
  };
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const step = e.shiftKey ? BIG_STEP : STEP;
    let next: number | null = null;
    if (e.key === "ArrowLeft") next = shown + step;
    else if (e.key === "ArrowRight") next = shown - step;
    else if (e.key === "Home") next = PANEL_MIN;
    else if (e.key === "End") next = max;
    if (next === null) return;
    e.preventDefault();
    set(next, true);
  };
  const reset = () => {
    setWidth(PANEL_DEFAULT);
    writeWidth(null);
  };

  return (
    <div
      ref={ref}
      className="pj-resizer no-print"
      role="separator"
      aria-orientation="vertical"
      aria-controls={PANEL_ID}
      aria-label="Lebar panel project"
      aria-valuenow={shown}
      aria-valuemin={PANEL_MIN}
      aria-valuemax={max}
      aria-valuetext={`${shown} piksel`}
      title="Geser untuk mengubah lebar panel. Klik dua kali untuk lebar semula."
      tabIndex={0}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={end}
      onPointerCancel={end}
      onKeyDown={onKeyDown}
      onDoubleClick={reset}
    >
      {/* A small grip, so the edge reads as something to drag. */}
      <span className="pj-resizer-grip" aria-hidden="true" />
    </div>
  );
}
